import IconsEnum from '@enums/icons.enum'
import { Campaign, Gamification, StringsHelper, Tag } from '@hub/shared'
import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import './rules.page.scoped.scss'

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  gamifications: Gamification[]
  campaign: Campaign
}

export default ({ gamifications, campaign }: Props) => {
  const [t, i18n] = useTranslation('engage')
  const stringsHelper = useMemo(() => new StringsHelper(), [])
  const iconsEnum = useMemo(() => IconsEnum, [])
  const referralGamification = useMemo(() => gamifications.find((g) => g.type === 'referral' && g.enabled), [gamifications])
  const spreadsheetGamification = useMemo(() => gamifications.find((g) => g.type === 'spreadsheet' && g.enabled), [gamifications])
  const socialNetworkGamification = useMemo(
    () => gamifications.find((g) => g.type === campaign.socialNetworks[0] && g.enabled),
    [gamifications, campaign]
  )
  const [hasSomeRule, setHasSomeRule] = useState({
    participate: false,
    stories: false,
    reels: false,
    video: false,
    tweet: false,
    retweet: false,
  })
  const mainSocialNetworkFields = [
    'stories',
    'comment',
    'commentReels',
    'like',
    'likeReels',
    'participate',
    'reels',
    'video',
    'tweet',
    'retweet',
    'view',
    'share',
  ]
  const contentsSocialNetworkFields = [
    {
      name: 'participate',
      metrics: ['comments', 'likes'],
    },
    {
      name: 'stories',
      metrics: [],
    },
    {
      name: 'reels',
      metrics: ['comments', 'likes'],
    },
    {
      name: 'video',
      metrics: ['views', 'likes', 'shares'],
    },
    {
      name: 'tweet',
      metrics: ['comments', 'likes'],
    },
    {
      name: 'retweet',
      metrics: [],
    },
  ]
  const rulesFields = ['max{{rule}}PerUser', 'max{{rule}}PerUserDay', 'max{{rule}}PerUserMonth', 'max{{rule}}PerUserWeek']

  const metricsFields = ['min{{metric}}Per{{field}}', 'max{{metric}}Per{{field}}']

  const icons = (field: string) => {
    switch (field) {
      case 'stories':
        return iconsEnum.stories
      case 'comment':
      case 'commentReels':
        return iconsEnum.comments
      case 'like':
      case 'likeReels':
        return iconsEnum.like
      case 'participate':
        return iconsEnum.post
      case 'reels':
        return iconsEnum.reels
      case 'video':
        return iconsEnum.video
      case 'tweet':
        return iconsEnum.tweet
      case 'retweet':
        return iconsEnum.retweet
      case 'view':
        return iconsEnum.view
      case 'share':
        return iconsEnum.share
    }
  }

  const getWeekNumber = useCallback(
    (weekName: string) => {
      const reference = [
        new Date('2022-01-02'),
        new Date('2022-01-03'),
        new Date('2022-01-04'),
        new Date('2022-01-05'),
        new Date('2022-01-06'),
        new Date('2022-01-07'),
        new Date('2022-01-08'),
      ]
      return stringsHelper.capitalizeFirstLetter(
        reference?.[weekName?.toLowerCase()]?.toLocaleDateString(i18n.language, { weekday: 'long', timeZone: 'UTC' })
      )
    },
    [i18n.language, stringsHelper]
  )

  return (
    <div className="engage-rules-page">
      {socialNetworkGamification && (
        <section>
          <h5>{t('rules.pointing.content')}</h5>
          <div className="items-details">
            <Tag
              icon={<i className={`${iconsEnum.startDate}`}></i>}
              style={{
                width: '100%',
                justifyContent: 'flex-start',
              }}
              wrapperStyle={{
                width: '100%',
              }}
              labels={[
                <span
                  style={{
                    fontSize: 'inherit',
                  }}
                  dangerouslySetInnerHTML={{
                    __html: t('rules.dates', {
                      startDate: new Date(
                        socialNetworkGamification?.time?.start?.replace('T00:00:00.000Z', 'T23:59:59.000Z')
                      ).toLocaleDateString(i18n.language),
                      endDate: new Date(socialNetworkGamification?.time?.end).toLocaleDateString(i18n.language),
                    }),
                  }}
                />,
              ]}
              separator
            />
            {socialNetworkGamification?.config?.[campaign?.socialNetworks[0]]?.onlyScoringApproved && (
              <Tag
                icon={<i className={`${iconsEnum.generalRanking}`}></i>}
                style={{
                  width: '100%',
                  justifyContent: 'flex-start',
                }}
                wrapperStyle={{
                  width: '100%',
                }}
                labels={[<span className="text-bold span-inherit">{t('rules.onlyScoringApproved')}</span>]}
                separator
              />
            )}
            {socialNetworkGamification?.config?.[campaign?.socialNetworks[0]]?.maxScore > 0 && (
              <Tag
                icon={<i className={`${iconsEnum.generalRanking}`}></i>}
                style={{
                  width: '100%',
                  justifyContent: 'flex-start',
                }}
                wrapperStyle={{
                  width: '100%',
                }}
                labels={[
                  <div>
                    {t('rules.maxScore')}:{' '}
                    <span className="text-bold span-inherit">
                      {socialNetworkGamification?.config?.[campaign?.socialNetworks[0]]?.maxScore?.toLocaleString(i18n.language)}{' '}
                      {t('points')}
                    </span>
                  </div>,
                ]}
                separator
              />
            )}
            {mainSocialNetworkFields?.map((field) =>
              socialNetworkGamification?.config[campaign.socialNetworks[0]][field] ? (
                <Tag
                  key={field}
                  icon={<i className={`${icons(field)}`}></i>}
                  style={{
                    width: '100%',
                    justifyContent: 'flex-start',
                  }}
                  wrapperStyle={{
                    width: '100%',
                  }}
                  labels={[
                    <div key={field}>
                      {t(`${field}`)}:{' '}
                      <strong>
                        {socialNetworkGamification?.config[campaign.socialNetworks[0]][field]} {t('points')}
                      </strong>
                    </div>,
                  ]}
                  separator
                />
              ) : null
            )}
          </div>
        </section>
      )}

      {spreadsheetGamification && (
        <section>
          <h5>{t('rules.pointing.spreadsheet')}</h5>
          <div className="items-details">
            {spreadsheetGamification?.actions?.map((action) =>
              action.value ? (
                <Tag
                  key={action?.key}
                  icon={<i className={`${iconsEnum.scoreRanking}`}></i>}
                  style={{
                    width: '100%',
                    justifyContent: 'flex-start',
                  }}
                  wrapperStyle={{
                    width: '100%',
                  }}
                  labels={[
                    <div>
                      {t(`rules.${action?.key}`)}:{' '}
                      <strong>
                        {action?.value} {t('points')}
                      </strong>
                    </div>,
                  ]}
                  subtag={action?.label}
                  separator
                />
              ) : null
            )}
          </div>
        </section>
      )}

      {referralGamification && (
        <section>
          <h5>{t('rules.pointing.referral')}</h5>
          <div className="items-details">
            <Tag
              icon={<i className={`${iconsEnum.startDate}`}></i>}
              style={{
                width: '100%',
                justifyContent: 'flex-start',
              }}
              wrapperStyle={{
                width: '100%',
              }}
              labels={[
                <span
                  style={{
                    fontSize: 'inherit',
                  }}
                  dangerouslySetInnerHTML={{
                    __html: t('rules.dates', {
                      startDate: new Date(
                        referralGamification?.time?.start?.replace('T00:00:00.000Z', 'T23:59:59.000Z')
                      ).toLocaleDateString(i18n.language),
                      endDate: new Date(referralGamification?.time?.end).toLocaleDateString(i18n.language),
                    }),
                  }}
                />,
              ]}
              separator
            />
            {referralGamification?.actions?.map((action) =>
              action.value ? (
                <Tag
                  key={action?.key}
                  icon={<i className={`${iconsEnum.referralRanking}`}></i>}
                  style={{
                    width: '100%',
                    justifyContent: 'flex-start',
                  }}
                  wrapperStyle={{
                    width: '100%',
                  }}
                  labels={[
                    <div>
                      {t(`rules.${action?.key}`)}:{' '}
                      <strong>
                        {action?.value} {t('points')}
                      </strong>
                    </div>,
                  ]}
                  subtag={action?.label}
                  separator
                />
              ) : null
            )}
          </div>
        </section>
      )}

      <section>
        <h5>{t('rules.pointing.main')}</h5>

        {socialNetworkGamification?.config[campaign?.socialNetworks[0]].tiebreakerCriteria && (
          <div>
            <h6>{t('rules.pointing.generalRules')}</h6>
            <div className="items-details">
              <Tag
                icon={<i className={`${iconsEnum.generalRanking}`}></i>}
                labels={[
                  `${t('rules.tiebreakerCriteria.label')} `,
                  <span className="text-bold span-inherit">
                    {t(
                      'rules.tiebreakerCriteria.' +
                        (campaign?.tiebreakerCriteria || socialNetworkGamification?.config[campaign?.socialNetworks[0]].tiebreakerCriteria)
                    )}
                  </span>,
                ]}
                separator
              />
            </div>
          </div>
        )}

        {(campaign?.weekRanking || socialNetworkGamification?.config[campaign?.socialNetworks[0]]?.weekRanking) && (
          <div>
            <h6>{t('rules.pointing.weeklyRules')}</h6>
            <div className="items-details">
              <Tag
                icon={<i className={`${iconsEnum.week}`}></i>}
                labels={[
                  `${t('rules.startOfWeek.label')} `,
                  <span className="text-bold span-inherit">{getWeekNumber(campaign?.startOfWeek)}</span>,
                ]}
                separator
              />
              {campaign?.totalGroup ? (
                <Tag
                  icon={<i className={`${iconsEnum.week}`}></i>}
                  labels={[
                    `${t('rules.totalGroup.label')} `,
                    <span className="text-bold span-inherit">
                      {campaign?.totalGroup} {t('week')}
                    </span>,
                  ]}
                  separator
                />
              ) : null}
            </div>
          </div>
        )}

        {socialNetworkGamification
          ? contentsSocialNetworkFields?.map((field) =>
              socialNetworkGamification.config[campaign.socialNetworks[0]][field.name] ? (
                <div key={field.name + '-detail'} className={`${!hasSomeRule[field.name] ? 'display-none' : ''}`}>
                  <h6>{t(`${field.name}`)}</h6>
                  <div className="items-details">
                    {rulesFields?.map((label) => {
                      let ruleFullName = `${label.replace('{{rule}}', stringsHelper.capitalizeFirstLetter(field.name))}`
                      if (field.name === 'participate') {
                        ruleFullName = `${label.replace('{{rule}}', stringsHelper.capitalizeFirstLetter('participation'))}`
                      }
                      if (socialNetworkGamification.config[campaign.socialNetworks[0]][ruleFullName] > 0) {
                        if (!hasSomeRule[field.name]) {
                          setHasSomeRule({ ...hasSomeRule, [field.name]: true })
                        }
                        return (
                          <span key={ruleFullName}>
                            <Tag
                              icon={<i className={`${icons(field.name)}`}></i>}
                              labels={[
                                `${t(`rules.${label}`, { rule: t(field.name) })} `,
                                <span className="text-bold span-inherit">
                                  {socialNetworkGamification.config[campaign.socialNetworks[0]][ruleFullName]?.toLocaleString(
                                    i18n.language
                                  )}
                                </span>,
                              ]}
                              separator
                            />
                          </span>
                        )
                      }
                    })}
                    {field?.metrics?.map((metric) =>
                      metricsFields?.map((label) => {
                        let ruleFullName = `${label
                          .replace('{{metric}}', stringsHelper.capitalizeFirstLetter(metric))
                          .replace('{{field}}', stringsHelper.capitalizeFirstLetter(field.name))}`
                        if (field.name === 'participate') {
                          if (label.startsWith('min') && metric === 'likes') {
                            ruleFullName = `${label
                              .replace('{{metric}}', stringsHelper.capitalizeFirstLetter(metric))
                              .replace('{{field}}', stringsHelper.capitalizeFirstLetter(field.name))
                              .replace('Per', '')}`
                          } else {
                            ruleFullName = `${label
                              .replace('{{metric}}', stringsHelper.capitalizeFirstLetter(metric))
                              .replace('{{field}}', stringsHelper.capitalizeFirstLetter('Post'))}`
                          }
                        }
                        return socialNetworkGamification.config[campaign.socialNetworks[0]][ruleFullName] > 0 ? (
                          <Tag
                            key={ruleFullName}
                            icon={<i className={`${icons(metric?.slice(0, metric?.length - 1))}`}></i>}
                            labels={[
                              `${t(`rules.${label}`, { metric: t(metric?.slice(0, metric?.length - 1)) })} `,
                              <span className="text-bold span-inherit">
                                {socialNetworkGamification.config[campaign.socialNetworks[0]][ruleFullName]?.toLocaleString(i18n.language)}
                              </span>,
                            ]}
                            separator
                          />
                        ) : null
                      })
                    )}
                  </div>
                </div>
              ) : null
            )
          : null}
      </section>
    </div>
  )
}
